/**
 * Two-tone surface recipes. Tokens live in styles/colors.css.
 *
 * Pages read in two tones: the canvas frames a page (its ground, header and
 * the gutters between blocks) and every content block is a white surface on
 * it. The sidebar theme adds one accent for a page's single highlight, and ink
 * carries primary actions and selection marks.
 *
 * Compose these instead of writing surface colors by hand, so the look can
 * change in one place.
 */

/**
 * Re-points the page-ground token at the surface, so anything inside a block
 * that paints `bg-background` (sticky table headers, inputs, group bands)
 * stays white instead of picking up the canvas, and turns tiles inside it to
 * the inset tone.
 */
export const SURFACE_SCOPE =
  '[--background:var(--surface-card)] [--surface-tile:var(--surface-inset)] [--shadow-tile:none]'

/** A white content block on the canvas, without a shape. */
export const SURFACE_BLOCK = `bg-surface shadow-block ${SURFACE_SCOPE}`

/**
 * A tile (overview field, stat): white with a lift on the canvas, the inset
 * tone flat inside a white surface. It sets no scope of its own.
 */
export const SURFACE_TILE =
  'bg-[hsl(var(--surface-tile))] shadow-[var(--shadow-tile)]'

/** The standard white card on the canvas. */
export const SURFACE_CARD = `${SURFACE_BLOCK} rounded-card`

/**
 * A page whose content is one working surface: a white sheet under the page
 * header that keeps the sheet gutter from the header and the canvas edges, so
 * its rounded corners never meet a hairline. `overflow: clip` rounds it
 * without making it a scroll container, so sticky toolbars inside still stick.
 * Use `<ContentSheet>`, or this on an element that is already the page root.
 */
export const CONTENT_SHEET = `${SURFACE_BLOCK} m-sheet h-[calc(100%-2*var(--sheet-gutter))] overflow-clip rounded-card`

/**
 * A flow page's scroll viewport. It keeps the sheet gutter from the canvas
 * edges, so a wide table scrolls inside the edges of the filter-bar sheet
 * above it instead of running past them.
 */
export const FLOW_VIEWPORT =
  'flow-viewport relative mx-sheet flex min-h-0 flex-1 flex-col'

/** A selected item in a menu or list: the theme accent tint with ink text. */
export const SELECTED_ITEM =
  'bg-theme-accent/45 text-foreground dark:bg-theme-accent/20'

/** Hover for an idle item on a surface. */
export const IDLE_ITEM_HOVER = 'hover:bg-surface-inset hover:text-foreground'

/** The page's one highlighted element: the theme accent fill. */
export const HIGHLIGHT_FILL = 'bg-theme-accent text-theme-accent-foreground'

/**
 * A white card inside a canvas popover or menu: the popover keeps a 6px
 * canvas gutter (p-1.5 / gap-1.5) and the card's corners stay concentric with
 * the popover's. No overflow clip, so inline dropdowns can escape it.
 */
export const POPOVER_CARD = `${SURFACE_BLOCK} rounded-control`

/**
 * A block set apart from the white ones beside it (a status or "what just
 * happened" card): a soft wash of the theme accent, softer than a selection.
 */
export const HIGHLIGHT_TINT = 'bg-theme-accent/25 dark:bg-theme-accent/10'

/** A count chip on the canvas; the active one takes the highlight. */
export const COUNT_CHIP = 'bg-surface text-muted-foreground'
export const COUNT_CHIP_ACTIVE = HIGHLIGHT_FILL

/**
 * A settings card: a hairline-bordered white block inside a white pane. Its
 * header is a quiet heading on the card itself — no fill band — with one
 * hairline above the body. Compose the header's own layout (flex, gaps) with
 * SETTINGS_CARD_HEADER; SettingsSection is the component form.
 */
export const SETTINGS_CARD =
  'overflow-hidden rounded-card border border-border/60 bg-surface'
export const SETTINGS_CARD_HEADER = 'border-b border-border/50 px-4 py-3'
/** The settings card header's icon: a quiet inset tile, not a tinted chip. */
export const SETTINGS_CARD_ICON =
  'flex h-8 w-8 flex-none items-center justify-center rounded-control bg-surface-inset text-foreground/70 [&>svg]:h-4 [&>svg]:w-4'
/** The danger-zone tone for a settings card and its icon tile. */
export const SETTINGS_CARD_DANGER = 'border-destructive/30'
export const SETTINGS_CARD_ICON_DANGER = 'bg-destructive/10 text-destructive'
